import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCents } from '@servwell/shared';
import { dateOnly } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ErrorState, GoneState, isNotFound } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { IconSchedule } from '../icons';
import { AGREEMENT_STATUS, display } from '../status';
import { scheduleText } from '../agreements/RepeatingWorkPanel';

/**
 * One piece of repeating work, and the visits it has booked (PRD v2 §4 — URL-real law).
 *
 * The occurrence list is the point of the page: "projected" is a date we intend to book,
 * "booked" is a real job on the schedule, "skipped" is a season somebody deliberately
 * passed on. Those are three different facts and they never collapse into one.
 */

const OCCURRENCE: Record<
  string,
  { label: string; tone: 'neutral' | 'info' | 'success' }
> = {
  projected: { label: 'Planned', tone: 'neutral' },
  generated: { label: 'Booked', tone: 'success' },
  skipped: { label: 'Skipped', tone: 'info' },
};

const BILLING_LABEL: Record<string, string> = {
  per_visit: 'each visit',
  monthly: 'monthly',
  annual: 'once a year',
};

export function AgreementDetailPage() {
  const { agreementId } = useParams({ from: '/office/agreements/$agreementId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery(trpc.agreement.get.queryOptions({ agreementId }));
  const customer = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: q.data?.agreement.customerId ?? '' }),
    enabled: Boolean(q.data?.agreement.customerId),
  });

  const done = () => {
    setError(null);
    void qc.invalidateQueries();
  };
  const opts = {
    onSuccess: done,
    onError: (e: { message: string }) => setError(e.message),
  };
  const activate = useMutation(trpc.agreement.activate.mutationOptions(opts));
  const pause = useMutation(trpc.agreement.pause.mutationOptions(opts));
  const resume = useMutation(trpc.agreement.resume.mutationOptions(opts));
  const cancel = useMutation(trpc.agreement.cancel.mutationOptions(opts));
  const generate = useMutation(trpc.agreement.generate.mutationOptions(opts));
  const skip = useMutation(trpc.agreement.skip.mutationOptions(opts));

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/jobs" label="Jobs" />
        {isNotFound(q.error) ? (
          <GoneState
            icon={IconSchedule}
            what="repeating job"
            backTo="/jobs"
            backLabel="Back to jobs"
          />
        ) : (
          <ErrorState
            icon={IconSchedule}
            what="this repeating job"
            onRetry={() => void q.refetch()}
          />
        )}
      </PageRoot>
    );
  }
  // The service returns null for a missing row rather than throwing — that is a gone
  // record, not a broken query, so it gets the same treatment as a NOT_FOUND.
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/jobs" label="Jobs" />
        <GoneState
          icon={IconSchedule}
          what="repeating job"
          backTo="/jobs"
          backLabel="Back to jobs"
        />
      </PageRoot>
    );
  }

  const { agreement: a, occurrences } = q.data;
  const d = display(AGREEMENT_STATUS, a.status);
  const customerName = customer.data?.customer.displayName ?? null;
  const active = a.status === 'active';
  const ended = a.status === 'canceled' || a.status === 'expired';
  const busy =
    activate.isPending || pause.isPending || resume.isPending || cancel.isPending;

  return (
    <PageRoot>
      <BackCrumb to="/jobs" label="Jobs" />
      <DetailHeader
        title={a.name}
        badge={<StatusBadge tone={d.tone}>{d.label}</StatusBadge>}
        right={
          ended ? null : (
            <>
              {/* A draft never appears today — the server creates these active — but the
                  state exists in the machine, so the one move out of it exists too. */}
              {a.status === 'draft' ? (
                <Button
                  disabled={busy}
                  onClick={() => activate.mutate({ agreementId, ...idem() })}
                >
                  Start it
                </Button>
              ) : null}
              {active ? (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => pause.mutate({ agreementId, ...idem() })}
                >
                  Pause
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => resume.mutate({ agreementId, ...idem() })}
                >
                  Resume
                </Button>
              )}
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => cancel.mutate({ agreementId, ...idem() })}
              >
                Stop it
              </Button>
            </>
          )
        }
      />
      <MetaStrip
        items={[
          { label: 'For', value: customerName ?? '—' },
          { label: 'Runs', value: scheduleText(a.schedule, a.startDate) },
          {
            label: 'Price',
            value: `${formatCents(a.priceCents)} ${BILLING_LABEL[a.billingMode] ?? a.billingMode}`,
          },
          { label: 'Since', value: dateOnly(a.startDate) },
        ]}
      />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {a.status === 'paused' ? (
        <Notice tone="warn">
          Paused — no new visits get booked until you resume it. Anything already on the
          schedule stays there.
        </Notice>
      ) : null}

      <SectionCard
        title="Visits"
        right={
          ended ? null : (
            <Button
              disabled={generate.isPending || !active}
              onClick={() =>
                generate.mutate({ agreementId, horizonDays: 365, ...idem() })
              }
            >
              {generate.isPending ? 'Booking…' : 'Book the next year'}
            </Button>
          )
        }
      >
        {occurrences.length === 0 ? (
          <EmptyState
            icon={IconSchedule}
            title="Nothing booked yet"
            description={
              active
                ? 'Book the next year and the visits land on the schedule, ready to assign.'
                : 'Resume this and you can book its visits.'
            }
          />
        ) : (
          <ul className="space-y-1 text-sm">
            {occurrences.map((o) => {
              const s = OCCURRENCE[o.status] ?? {
                label: o.status,
                tone: 'neutral' as const,
              };
              return (
                <li
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-1.5 last:border-0"
                >
                  <span className="font-medium tabular-nums">
                    {dateOnly(o.occurrenceKey, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                    {/* Only a planned visit can be skipped — one already on the schedule
                        gets cancelled as a job, where the customer conversation lives. */}
                    {o.status === 'projected' && !ended ? (
                      <Button
                        variant="ghost"
                        disabled={skip.isPending}
                        onClick={() =>
                          skip.mutate({
                            agreementId,
                            occurrenceKey: o.occurrenceKey,
                            ...idem(),
                          })
                        }
                      >
                        Skip
                      </Button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </PageRoot>
  );
}
